import { useState, useRef, useCallback, useEffect } from 'react';

const THUMB = 20;
const HALF = THUMB / 2;

export const DualRangeSlider = ({ min = 0, max = 2000000, step = 1000, value = [0, 2000000], onChange, onCommit }) => {
  const [renderVal, setRenderVal] = useState(value);
  const valRef = useRef(value);
  const trackRef = useRef(null);
  const isDragging = useRef(false);

  useEffect(() => {
    if (!isDragging.current) {
      valRef.current = value;
      setRenderVal(value);
    }
  }, [value]);

  const pct = (v) => ((v - min) / (max - min)) * 100;

  const valFromX = useCallback((clientX) => {
    if (!trackRef.current) return min;
    const rect = trackRef.current.getBoundingClientRect();
    const usable = rect.width - THUMB;
    const x = clientX - rect.left - HALF;
    const ratio = Math.max(0, Math.min(1, x / usable));
    const raw = min + ratio * (max - min);
    return Math.round(raw / step) * step;
  }, [min, max, step]);

  const handleDrag = useCallback((thumb) => (e) => {
    e.preventDefault();
    isDragging.current = true;

    const move = (ev) => {
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const v = valFromX(x);
      const prev = valRef.current;
      let next;
      if (thumb === 'min') {
        const clamped = Math.max(min, Math.min(v, prev[1] - step));
        next = [clamped, prev[1]];
      } else {
        const clamped = Math.min(max, Math.max(v, prev[0] + step));
        next = [prev[0], clamped];
      }
      valRef.current = next;
      setRenderVal(next);
      if (onChange) onChange(next);
    };

    const up = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', up);
      if (onCommit) onCommit();
    };

    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', up);
  }, [valFromX, min, max, step, onChange, onCommit]);

  // Thumb left: at 0% → 0px, at 100% → calc(100% - 20px)
  const thumbLeft = (v) => `calc(${pct(v)}% - ${pct(v) * THUMB / 100}px)`;
  // Track fill center of thumb: offset by half thumb
  const fillPos = (v) => `calc(${pct(v)}% - ${pct(v) * THUMB / 100}px + ${HALF}px)`;

  return (
    <div
      ref={trackRef}
      className="relative w-full h-10 flex items-center select-none touch-none"
      data-testid="filter-precio-slider"
    >
      {/* Track bg */}
      <div
        className="absolute h-1.5 rounded-full bg-primary/20"
        style={{ left: `${HALF}px`, right: `${HALF}px` }}
      />
      {/* Active range */}
      <div
        className="absolute h-1.5 rounded-full bg-primary"
        style={{ left: fillPos(renderVal[0]), right: `calc(100% - ${fillPos(renderVal[1])})` }}
      />
      {/* Min thumb */}
      <div
        className="absolute w-5 h-5 rounded-full border-2 border-primary bg-background shadow-lg cursor-grab active:cursor-grabbing active:scale-110 transition-transform z-10 hover:ring-2 hover:ring-primary/40"
        style={{ left: thumbLeft(renderVal[0]), top: '50%', transform: 'translateY(-50%)' }}
        onMouseDown={handleDrag('min')}
        onTouchStart={handleDrag('min')}
        role="slider"
        aria-label="Precio mínimo"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={renderVal[0]}
        tabIndex={0}
      />
      {/* Max thumb */}
      <div
        className="absolute w-5 h-5 rounded-full border-2 border-primary bg-background shadow-lg cursor-grab active:cursor-grabbing active:scale-110 transition-transform z-10 hover:ring-2 hover:ring-primary/40"
        style={{ left: thumbLeft(renderVal[1]), top: '50%', transform: 'translateY(-50%)' }}
        onMouseDown={handleDrag('max')}
        onTouchStart={handleDrag('max')}
        role="slider"
        aria-label="Precio máximo"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={renderVal[1]}
        tabIndex={0}
      />
    </div>
  );
};
